import normalizeUrlLib from "normalize-url";

/**
 * Normalizes a raw URL by:
 * - Prepending https:// if no protocol
 * - Preserving http:// if explicitly provided (or upgrading if forceHttps is true)
 * - Prepending www. ONLY for bare domains (2-part hostnames, e.g. "example.com")
 * - Preserving existing subdomains (e.g. "blog.example.com" stays as-is)
 * - Leaving existing www intact
 *
 * @param {string} raw - The raw user-entered URL string
 * @param {object} [options] - Optional overrides for normalize-url
 * @returns {string} The normalized URL
 */
export function normalizeUrl(raw, options = {}) {
  if (!raw || typeof raw !== "string") return raw;

  let url = raw.trim();
  if (!url) return url;

  // Step 1: Use normalize-url for base cleanup (protocol, encoding, sorting, etc.)
  // stripWWW: false — we handle www ourselves, don't let the library strip it
  // forceHttps: default false — allows http:// without forcefully upgrading
  // defaultProtocol: "https" — defaults bare domains (e.g. "example.com") to https
  // removeTrailingSlash: false — preserve user intent for paths
  const forceHttps = options.forceHttps ?? false;

  let normalized;
  try {
    normalized = normalizeUrlLib(url, {
      stripWWW: false,
      forceHttps,
      defaultProtocol: "https",
      removeTrailingSlash: false,
      ...options,
    });
  } catch {
    // If normalize-url can't parse it, fall back to the trimmed raw input
    normalized = url;
  }

  // Step 2: Smart www prepending — only for bare domains (exactly 2 parts)
  try {
    const parsed = new URL(normalized);
    const hostname = parsed.hostname;
    const parts = hostname.split(".");

    // If hostname has exactly 2 parts (bare domain like "example.com")
    // AND doesn't already start with "www", prepend "www."
    if (parts.length === 2 && parts[0] !== "www") {
      parsed.hostname = "www." + hostname;
      return parsed.href;
    }
  } catch {
    // If URL parsing fails, return the normalized string as-is
  }

  return normalized;
}

export default normalizeUrl;
